import { MOSIDA_API_BASE_URL } from "../config/apiUrls";
import { fetchWithRetry } from "../utils/httpClient";

const baseUrl = MOSIDA_API_BASE_URL;

export interface OrderWithPaymentPayload {
  orderId: string;
  image_url: string;
}

export interface OrderServiceResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export async function updateOrderWithPayment(
  orderId: string,
  payload: OrderWithPaymentPayload
): Promise<OrderServiceResponse> {
  try {
    const response = await fetchWithRetry(`${baseUrl}/product/order/${orderId}/with-payment`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const raw = await response.text();
    let json: Record<string, unknown> = {};
    try {
      json = raw ? JSON.parse(raw) : {};
    } catch {
      json = { message: raw };
    }

    if (!response.ok) {
      const message =
        (typeof json.message === "string" && json.message) ||
        (typeof json.error === "string" && json.error) ||
        `HTTP error! status: ${response.status}`;
      return { success: false, error: message };
    }

    return {
      success: true,
      data: json,
      message: typeof json.message === "string" ? json.message : undefined,
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Network error";
    console.error("Update order with payment error:", error);
    return { success: false, error: message };
  }
}

export interface PayOnReceptionOrderPayload {
  temp_data_id: string;
  email?: string;
}

export interface PayOnReceptionOrder {
  id?: string;
  orderNumber?: string | number;
  totalAmount?: string | number;
  status?: string;
  [key: string]: unknown;
}

/** Create unpaid orders (status payment-pending) to settle at reception. */
export async function createPayOnReceptionOrder(
  payload: PayOnReceptionOrderPayload,
  type: "onsite" | "offsite" = "onsite"
): Promise<OrderServiceResponse<PayOnReceptionOrder[]>> {
  try {
    const response = await fetchWithRetry(
      `${baseUrl}/product/order/pay-on-reception?type=${encodeURIComponent(type)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const raw = await response.text();
    let json: unknown = {};
    try {
      json = raw ? JSON.parse(raw) : {};
    } catch {
      json = { message: raw };
    }

    if (!response.ok) {
      const errorObj = json as Record<string, unknown>;
      const message =
        (typeof errorObj.message === "string" && errorObj.message) ||
        (typeof errorObj.error === "string" && errorObj.error) ||
        `HTTP error! status: ${response.status}`;
      return { success: false, error: message };
    }

    const orders = Array.isArray(json)
      ? (json as PayOnReceptionOrder[])
      : Array.isArray((json as { data?: unknown }).data)
        ? (json as { data: PayOnReceptionOrder[] }).data
        : [];

    return {
      success: true,
      data: orders,
      message:
        typeof (json as { message?: unknown }).message === "string"
          ? (json as { message: string }).message
          : undefined,
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Network error";
    console.error("Pay on reception order error:", error);
    return { success: false, error: message };
  }
}
